/**
 * Single source of truth for all application routes
 * Used throughout the app for navigation and E2E testing
 */

// Public routes
export const ROUTES = {
  // Public
  HOME: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  RESET_PASSWORD: '/reset-password',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  HELP: '/help',
  PUBLIC_WORKOUT: '/w/:slug',

  // App routes (auth required)
  DASHBOARD: '/dashboard',
  PLANNER: '/planner',
  LIBRARY: '/library',
  MARKETPLACE: '/marketplace',
  PROFILE: '/profile',
  PROGRESS: '/progress',
  HISTORY: '/history',
  EXERCISES: '/exercises',
  WALLET: '/wallet',
  AI_COACH: '/ai-coach',
  NUTRITION: '/nutrition',
  FEEDBACK: '/feedback',
  SOCIAL: '/social',
  IMPORT: '/import',
  SUBSCRIPTION_SUCCESS: '/subscription/success',
  SUBSCRIPTION_CANCEL: '/subscription/cancel',
  
  // Admin routes
  ADMIN: '/admin',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_USERS: '/admin/users',
  ADMIN_ASSETS: '/admin/assets',
  ADMIN_MEDIA: '/admin/media',
  ADMIN_EXERCISES: '/admin/exercises',
  ADMIN_WORKOUTS: '/admin/workouts',
  ADMIN_MARKETPLACE: '/admin/marketplace',
  ADMIN_CREATORS: '/admin/creators',
  ADMIN_ECONOMY: '/admin/economy',
  ADMIN_CHALLENGES: '/admin/challenges',
  ADMIN_SYSTEM: '/admin/system',
  ADMIN_INTEGRITY: '/admin/integrity',
  ADMIN_INSIGHTS: '/admin/insights',
  ADMIN_SETTINGS: '/admin/settings',
} as const;

// Dynamic route builders
export const buildSessionRoute = (sessionId: string) => `/session/${sessionId}`;
// The detail page is mounted at /exercises/:exerciseId/stats (App.tsx);
// the bare /exercises/:id form 404s.
export const buildExerciseDetailRoute = (exerciseId: string) => `/exercises/${exerciseId}/stats`;
export const buildProgramDetailRoute = (programId: string) => `/marketplace/programs/${programId}`;
export const buildPublicWorkoutRoute = (slug: string) => `/w/${slug}`;

// Static path prefix for the public workout share page (the :slug part
// is dynamic). Useful for route guards that want to allow `/w/...`.
export const PUBLIC_WORKOUT_PATH_PREFIX = '/w' as const;

// Route categories for testing
export const PUBLIC_ROUTES = [
  ROUTES.HOME,
  ROUTES.LOGIN,
  ROUTES.SIGNUP,
  ROUTES.RESET_PASSWORD,
  ROUTES.TERMS,
  ROUTES.PRIVACY,
  PUBLIC_WORKOUT_PATH_PREFIX,
] as const;

export const AUTH_REQUIRED_ROUTES = [
  ROUTES.DASHBOARD,
  ROUTES.PLANNER,
  ROUTES.LIBRARY,
  ROUTES.MARKETPLACE,
  ROUTES.PROFILE,
  ROUTES.PROGRESS,
  ROUTES.HISTORY,
  ROUTES.EXERCISES,
  ROUTES.WALLET,
  ROUTES.AI_COACH,
  ROUTES.NUTRITION,
  ROUTES.FEEDBACK,
  ROUTES.IMPORT,
] as const;

export const ADMIN_ROUTES = [
  ROUTES.ADMIN_DASHBOARD,
  ROUTES.ADMIN_USERS,
  ROUTES.ADMIN_ASSETS,
  ROUTES.ADMIN_MEDIA,
  ROUTES.ADMIN_EXERCISES,
  ROUTES.ADMIN_WORKOUTS,
  ROUTES.ADMIN_MARKETPLACE,
  ROUTES.ADMIN_CREATORS,
  ROUTES.ADMIN_ECONOMY,
  ROUTES.ADMIN_CHALLENGES,
  ROUTES.ADMIN_SYSTEM,
  ROUTES.ADMIN_INTEGRITY,
  ROUTES.ADMIN_INSIGHTS,
  ROUTES.ADMIN_SETTINGS,
] as const;

// Main navigation items (for sidebar/tabbar validation)
export const MAIN_NAV_ROUTES = [
  ROUTES.DASHBOARD,
  ROUTES.LIBRARY,
  ROUTES.PROGRESS,
  ROUTES.AI_COACH,
  ROUTES.EXERCISES,
] as const;

export type RouteKey = keyof typeof ROUTES;
export type Route = typeof ROUTES[RouteKey];
